import { Icon } from '@evisort/eds/components/Icon/Icon';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Text } from '@evisort/eds/components/Text/Text';
import { defineMessages, useIntl } from 'react-intl';

import { validatePassword } from '~/utils/password';

import styles from './PasswordRequirementsList.module.css';

const messages = defineMessages({
  capitalLetterRequirement: {
    id: 'PasswordRequirementsList.capitalLetterRequirement',
    defaultMessage: 'At least one capital letter (A-Z)',
    description: 'Password requirement: capital letter',
  },
  lowercaseLetterRequirement: {
    id: 'PasswordRequirementsList.lowercaseLetterRequirement',
    defaultMessage: 'At least one lowercase letter (a-z)',
    description: 'Password requirement: lowercase letter',
  },
  minimumCharactersRequirement: {
    id: 'PasswordRequirementsList.minimumCharactersRequirement',
    defaultMessage: 'At least 15 characters',
    description: 'Password requirement: minimum characters',
  },
  noIdenticalRunRequirement: {
    id: 'PasswordRequirementsList.noIdenticalRunRequirement',
    defaultMessage: 'No more than 3 identical characters in a row',
    description: 'Password requirement: no identical run',
  },
  noSequentialRunRequirement: {
    id: 'PasswordRequirementsList.noSequentialRunRequirement',
    defaultMessage: 'No more than 3 consecutive characters in a sequence',
    description: 'Password requirement: no sequential run',
  },
  numberRequirement: {
    id: 'PasswordRequirementsList.numberRequirement',
    defaultMessage: 'At least one number (0-9)',
    description: 'Password requirement: number',
  },
  specialCharacterRequirement: {
    id: 'PasswordRequirementsList.specialCharacterRequirement',
    defaultMessage: 'At least one special character',
    description: 'Password requirement: special character',
  },
});

const PASSWORD_REQUIREMENTS = [
  {
    key: 'satisfiesMinimumCharacters',
    message: messages.minimumCharactersRequirement,
  },
  {
    key: 'satisfiesCapitalCharacter',
    message: messages.capitalLetterRequirement,
  },
  {
    key: 'satisfiesLowercaseCharacter',
    message: messages.lowercaseLetterRequirement,
  },
  { key: 'satisfiesNumberCharacter', message: messages.numberRequirement },
  {
    key: 'satisfiesSpecialCharacter',
    message: messages.specialCharacterRequirement,
  },
  {
    key: 'satisfiesNoIdenticalRun',
    message: messages.noIdenticalRunRequirement,
  },
  {
    key: 'satisfiesNoSequentialRun',
    message: messages.noSequentialRunRequirement,
  },
] as const;

/** Whether `password` satisfies every rule the requirements list shows. */
export function isPasswordFullyValid(password: string) {
  return Object.values(validatePassword(password)).every(Boolean);
}

type RequirementStatus = 'empty' | 'met' | 'unmet';

function resolveRequirementStatus(
  password: string,
  satisfied: boolean,
): RequirementStatus {
  if (satisfied) return 'met';
  if (!password) return 'empty';
  return 'unmet';
}

const INDICATOR_CLASS_NAME = {
  empty: styles.indicatorEmpty,
  met: styles.indicatorMet,
  unmet: styles.indicatorUnmet,
} as const;

const REQUIREMENT_ICON = {
  met: 'check',
  unmet: 'x',
} as const;

function PasswordRequirement({
  password,
  satisfied,
  text,
}: {
  password: string;
  satisfied: boolean;
  text: string;
}) {
  const status = resolveRequirementStatus(password, satisfied);

  return (
    <Layout align="center" as="li" className={styles.requirement} spacing="xs">
      <span className={`${styles.indicator} ${INDICATOR_CLASS_NAME[status]}`}>
        {status !== 'empty' && (
          <Icon key={status} icon={REQUIREMENT_ICON[status]} size="xs" />
        )}
      </span>
      <Text
        as="span"
        tone={status === 'met' ? 'auto' : 'subdued'}
        variant="body-s"
      >
        {text}
      </Text>
    </Layout>
  );
}

/** Live checklist of the password rules, marking each as empty, unmet or met. */
export function PasswordRequirementsList({ password }: { password: string }) {
  const intl = useIntl();
  const validations = validatePassword(password);

  return (
    <Layout
      as="ul"
      className={styles.requirements}
      direction="column"
      spacing="xs"
    >
      {PASSWORD_REQUIREMENTS.map(({ key, message }) => (
        <PasswordRequirement
          key={key}
          password={password}
          satisfied={Boolean(validations[key])}
          text={intl.formatMessage(message)}
        />
      ))}
    </Layout>
  );
}
